<template>
  <!-- Compact insurance-verification status chip for the Patient Profile header.
       Renders nothing unless it has a status to show (the parent only mounts it
       when the soft-gated patient-status endpoint returns enabled:true). -->
  <span
    class="iv-chip"
    :class="{ 'is-stale': isStale }"
    :style="{ background: chip.bg, color: chip.color }"
    role="button"
    tabindex="0"
    :title="tooltip"
    @click="$emit('navigate')"
    @keydown.enter="$emit('navigate')"
  >
    <i class="fa" :class="chip.icon" aria-hidden="true"></i>
    <span class="iv-chip-label">Insurance: {{ chip.label }}</span>
    <span v-if="checkedLabel" class="iv-chip-meta">· {{ checkedLabel }}</span>
    <i class="fa fa-external-link iv-chip-go" aria-hidden="true"></i>
  </span>
</template>

<script>
/* eslint-disable */
import { statusPresentation, formatCheckedAt, relativeFromNow } from './constants.js';

export default {
  name: 'InsuranceStatusChip',
  props: {
    status: { type: String, default: null },       // 'verified' | 'inactive' | 'unable_to_verify' | 'error' | null
    lastVerified: { type: String, default: null },  // newest SUCCESSFUL check / OD date, or null
    lastChecked: { type: String, default: null },   // latest check of any status (absent on an older backend)
    isStale: { type: Boolean, default: false },
    neverVerified: { type: Boolean, default: false },
    timezone: { type: String, default: '' },
  },
  computed: {
    chip() {
      // neverVerified with no status → the muted "Not verified" bucket.
      if (this.neverVerified && !this.status) return statusPresentation(null, {});
      return statusPresentation(this.status, {});
    },
    checkedLabel() {
      if (this.neverVerified || !this.lastVerified) return '';
      return relativeFromNow(this.lastVerified, this.timezone);
    },
    tooltip() {
      const checked = this.lastChecked && this.lastChecked !== this.lastVerified
        ? ` · last checked ${formatCheckedAt(this.lastChecked, this.timezone)}`
        : '';
      if (this.neverVerified || !this.lastVerified) {
        return checked ? `Never verified${checked} — open Insurance Verification` : 'Open Insurance Verification';
      }
      const stale = this.isStale ? ' (needs re-verification)' : '';
      return `Last verified ${formatCheckedAt(this.lastVerified, this.timezone)}${stale}${checked} — open Insurance Verification`;
    },
  },
};
</script>

<style lang="scss" scoped>
.iv-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
  max-width: 100%;
  transition: filter 0.12s ease;

  &:hover { filter: brightness(0.96); }
  &.is-stale { box-shadow: inset 0 0 0 1px rgba(229, 72, 77, 0.4); }
}

.iv-chip-label {
  white-space: nowrap;
}

.iv-chip-meta {
  font-weight: 600;
  opacity: 0.85;
  white-space: nowrap;
}

.iv-chip-go {
  font-size: 10px;
  opacity: 0.7;
}
</style>
